<template>
  <div class="mx-4 mt-4">
    <!-- Filters Section -->
    <div class="card mb-4">
      <div class="card-header border-0">
        <h3 class="mb-0">{{ $t("WEEKLY_REPORTS_REPORT.TITLE") }}</h3>
      </div>
      <div class="card-body pt-0">
        <div class="row">
          <div
            class="col-md-3"
            v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)"
          >
            <base-input :label="$t('COMMON.ORGANIZATION')">
              <organization-selector
                :organization="filters.organization_id"
                :filterable="true"
                :showAll="false"
                @organizationChanged="
                  (organizationId) => {
                    filters.organization_id = organizationId;
                    filters.establishment_id = null;
                    filters.service_center_id = null;
                  }
                "
              />
            </base-input>
          </div>
          <div class="col-md-3">
            <base-input :label="$t('COMMON.ESTABLISHMENT')">
              <establishment-selector
                :establishment="filters.establishment_id"
                :filterOrganization="filters.organization_id"
                :filterable="true"
                :showAll="true"
                @establishmentChanged="
                  (establishmentId) => {
                    filters.establishment_id = establishmentId;
                  }
                "
              />
            </base-input>
          </div>
          <div class="col-md-3">
            <base-input :label="$t('COMMON.SERVICECENTER')">
              <service-center-selector
                :serviceCenter="filters.service_center_id"
                :filterOrganization="filters.organization_id"
                :filterable="true"
                :showAll="true"
                @serviceCenterChanged="
                  (serviceCenterId) => {
                    filters.service_center_id = serviceCenterId;
                  }
                "
              />
            </base-input>
          </div>
          <div class="col-md-3">
            <base-input :label="$t('COMMON.STATUS')">
              <el-select
                v-model="filters.status"
                :placeholder="$t('COMMON.STATUS')"
              >
                <el-option
                  :label="$t('WEEKLY_REPORTS.REPORT_VALIDATED')"
                  value="VALIDATED"
                />
                <el-option
                  :label="$t('WEEKLY_REPORTS.REPORT_DRAFT')"
                  value="DRAFT"
                />
                <el-option :label="$t('COMMON.ALL')" value="ALL" />
              </el-select>
            </base-input>
          </div>
        </div>
        <div class="row">
          <div class="col-md-4">
            <base-input :label="$t('COMMON.DATE_RANGE')">
              <flat-picker
                v-model="dateRange"
                :config="dateRangeConfig"
                class="form-control"
                :placeholder="$t('COMMON.DATES_PLACEHOLDER')"
              />
            </base-input>
          </div>
          <div class="col-md-8 d-flex align-items-end pb-3">
            <base-button type="primary" @click="applyFilters">
              {{ $t("COMMON.APPLY_FILTERS") }}
            </base-button>
            <base-button type="secondary" @click="resetFilters">
              {{ $t("COMMON.RESET") }}
            </base-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Category Summary Cards -->
    <div class="row align-items-center mb-2">
      <div class="col">
        <h4 class="mb-0 text-muted text-uppercase">{{ $t("COMMON.SUMMARY") }}</h4>
      </div>
      <div class="col-auto">
        <span class="text-muted">
          {{ $t("WEEKLY_REPORTS_REPORT.NUMBER_OF_REPORTS") }}:
          <strong>{{ formatNumber(stats.numberOfReports) }}</strong>
        </span>
      </div>
    </div>
    <div class="row mb-4">
      <!-- Ventes -->
      <div class="col-xl col-lg-4 col-md-6 mb-3">
        <div class="card h-100 shadow-sm category-summary-card">
          <div class="card-header text-white cat-green">
            <h4 class="mb-1 text-white">{{ $t("WEEKLY_REPORTS_REPORT.SALES") }}</h4>
            <div class="h5 mb-0 font-weight-bold text-white">
              {{ formatCurrency(stats.totalSales) }}
            </div>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-7 text-muted">
                {{ $t("WEEKLY_REPORTS_REPORT.AVG_PER_WEEK") }}
              </div>
              <div class="col-5 font-weight-bold text-right">
                {{ formatCurrency(stats.weeklyAverageSales) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Achats -->
      <div class="col-xl col-lg-4 col-md-6 mb-3">
        <div class="card h-100 shadow-sm category-summary-card">
          <div class="card-header text-white cat-green">
            <h4 class="mb-1 text-white">{{ $t("WEEKLY_REPORTS_REPORT.PURCHASES") }}</h4>
            <div class="h5 mb-0 font-weight-bold text-white">
              {{ formatCurrency(stats.totalPurchases) }}
            </div>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-7 text-muted">
                {{ $t("WEEKLY_REPORTS_REPORT.AVG_PURCHASES_RATIO") }}
              </div>
              <div class="col-5 font-weight-bold text-right">
                {{ formatPercent(stats.avgPurchasesRatio) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Achats comestibles -->
      <div class="col-xl col-lg-4 col-md-6 mb-3">
        <div class="card h-100 shadow-sm category-summary-card">
          <div class="card-header text-white cat-green">
            <h4 class="mb-1 text-white">
              {{ $t("WEEKLY_REPORTS_REPORT.PURCHASES_CONSUMABLES") }}
            </h4>
            <div class="h5 mb-0 font-weight-bold text-white">
              {{ formatCurrency(stats.totalConsumables) }}
            </div>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-7 text-muted">
                {{ $t("WEEKLY_REPORTS_REPORT.AVG_RATIO") }}
              </div>
              <div class="col-5 font-weight-bold text-right">
                {{ formatPercent(stats.avgConsumablesRatio) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Salaires -->
      <div class="col-xl col-lg-4 col-md-6 mb-3">
        <div class="card h-100 shadow-sm category-summary-card">
          <div class="card-header text-white cat-green">
            <h4 class="mb-1 text-white">{{ $t("WEEKLY_REPORTS_REPORT.WAGES") }}</h4>
            <div class="h5 mb-0 font-weight-bold text-white">
              {{ formatCurrency(stats.totalWages) }}
            </div>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-7 text-muted">
                {{ $t("WEEKLY_REPORTS_REPORT.AVG_WAGES_RATIO") }}
              </div>
              <div class="col-5 font-weight-bold text-right">
                {{ formatPercent(stats.avgWagesRatio) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Loyer -->
      <div class="col-xl col-lg-4 col-md-6 mb-3">
        <div class="card h-100 shadow-sm category-summary-card">
          <div class="card-header text-white cat-green">
            <h4 class="mb-1 text-white">{{ $t("WEEKLY_REPORTS_REPORT.RENT") }}</h4>
            <div class="h5 mb-0 font-weight-bold text-white">
              {{ formatCurrency(stats.totalRent) }}
            </div>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-7 text-muted">
                {{ $t("WEEKLY_REPORTS_REPORT.AVG_RENT_RATIO") }}
              </div>
              <div class="col-5 font-weight-bold text-right">
                {{ formatPercent(stats.avgRentRatio) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Profits (presented last) -->
      <div class="col-xl col-lg-4 col-md-6 mb-3">
        <div class="card h-100 shadow-sm category-summary-card">
          <div class="card-header text-white cat-green">
            <h4 class="mb-1 text-white">{{ $t("WEEKLY_REPORTS_REPORT.PROFITS") }}</h4>
            <div class="h5 mb-0 font-weight-bold text-white">
              {{ formatCurrency(stats.netProfit) }}
            </div>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-7 text-muted">
                {{ $t("WEEKLY_REPORTS_REPORT.AVG_NET_PROFIT_RATIO") }}
              </div>
              <div class="col-5 font-weight-bold text-right">
                {{ formatPercent(stats.avgNetProfitRatio) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts Section -->
    <div class="row mb-4">
      <div class="col-12 mb-4">
        <div class="card h-100">
          <div class="card-header">
            <div class="row align-items-center">
              <div class="col">
                <h5 class="h3 mb-0">{{ $t('WEEKLY_REPORTS_REPORT.EVOLUTION_OVER_TIME') }}</h5>
              </div>
              <div class="col-auto">
                <el-select v-model="chartGrouping" size="small" @change="updateCharts">
                  <el-option value="weekly" :label="$t('COMMON.WEEKLY')" />
                  <el-option value="monthly" :label="$t('COMMON.MONTHLY')" />
                </el-select>
              </div>
            </div>
          </div>
          <div class="card-body">
            <div class="chart" v-loading="chartLoading">
              <line-chart
                v-if="evolutionChartData.labels.length > 0"
                :height="350"
                :chart-data="evolutionChartData"
                :extra-options="lineChartOptions"
              />
              <div v-else class="text-center text-muted py-5">
                {{ $t('COMMON.NO_DATA') }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 mb-4">
        <div class="card h-100">
          <div class="card-header">
            <h5 class="h3 mb-0">{{ $t('WEEKLY_REPORTS_REPORT.RATIOS_EVOLUTION_OVER_TIME') }}</h5>
          </div>
          <div class="card-body">
            <div class="chart" v-loading="chartLoading">
              <line-chart
                v-if="ratiosChartData.labels.length > 0"
                :height="350"
                :chart-data="ratiosChartData"
                :extra-options="ratiosLineChartOptions"
              />
              <div v-else class="text-center text-muted py-5">
                {{ $t('COMMON.NO_DATA') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Data Table Card -->
    <div class="card col-12">
      <div class="card-body">
        <el-table
          class="align-items-center table-flush"
          header-row-class-name="thead-light"
          :data="reportData"
          v-loading="loading"
          show-summary
          :summary-method="getSummaries"
        >
          <div slot="empty" v-if="loading">
            <img src="/img/loading.gif" style="height: 100px; width: 100px" />
          </div>

          <el-table-column
            :label="$t('WEEKLY_REPORTS_REPORT.PERIOD')"
            min-width="180px"
          >
            <template v-slot="{ row }">
              {{ formatDate(row.starts_at) }} &rarr; {{ formatDate(row.ends_at) }}
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('COMMON.ESTABLISHMENT')"
            prop="establishment_name"
            min-width="150px"
          />

          <el-table-column
            :label="$t('COMMON.CAFETERIA')"
            prop="cafeteria_name"
            min-width="140px"
          />

          <el-table-column
            :label="$t('WEEKLY_REPORTS_REPORT.TOTAL_SALES')"
            prop="total_sales"
            min-width="130px"
            align="right"
          >
            <template v-slot="{ row }">
              {{ formatCurrency(row.total_sales) }}
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('WEEKLY_REPORTS_REPORT.TOTAL_PURCHASES')"
            prop="total_purchases"
            min-width="130px"
            align="right"
          >
            <template v-slot="{ row }">
              {{ formatCurrency(row.total_purchases) }}
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('WEEKLY_REPORTS_REPORT.NET_PROFIT')"
            prop="net_profit"
            min-width="130px"
            align="right"
          >
            <template v-slot="{ row }">
              {{ formatCurrency(row.net_profit) }}
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('WEEKLY_REPORTS_REPORT.WAGES')"
            prop="wages"
            min-width="120px"
            align="right"
          >
            <template v-slot="{ row }">
              {{ formatCurrency(row.wages) }}
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('WEEKLY_REPORTS_REPORT.RENT')"
            prop="rent"
            min-width="110px"
            align="right"
          >
            <template v-slot="{ row }">
              {{ formatCurrency(row.rent) }}
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('WEEKLY_REPORTS_REPORT.WAGES_RATIO')"
            prop="wages_ratio"
            min-width="120px"
            align="right"
          >
            <template v-slot="{ row }">
              {{ formatPercent(row.wages_ratio) }}
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('WEEKLY_REPORTS_REPORT.RENT_RATIO')"
            prop="rent_ratio"
            min-width="120px"
            align="right"
          >
            <template v-slot="{ row }">
              {{ formatPercent(row.rent_ratio) }}
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('WEEKLY_REPORTS_REPORT.PURCHASES_RATIO')"
            prop="purchases_ratio"
            min-width="120px"
            align="right"
          >
            <template v-slot="{ row }">
              {{ formatPercent(row.purchases_ratio) }}
            </template>
          </el-table-column>

          <el-table-column
            :label="$t('WEEKLY_REPORTS_REPORT.NET_PROFIT_RATIO')"
            prop="net_profit_ratio"
            min-width="120px"
            align="right"
          >
            <template v-slot="{ row }">
              {{ formatPercent(row.net_profit_ratio) }}
            </template>
          </el-table-column>
        </el-table>

        <!-- Pagination -->
        <div
          class="mt-5 col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
        >
          <div class="">
            <p class="card-category">
              {{
                $t("COMMON.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES", {
                  from: total ? from + 1 : 0,
                  to: to,
                  of: total,
                })
              }}
            </p>
          </div>
          <base-pagination
            class="pagination-no-border"
            v-model="pagination.currentPage"
            :per-page="pagination.perPage"
            :total="total"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import {
  Table,
  TableColumn,
  Select,
  Option,
} from "element-ui";
import { BasePagination } from "@/components";
import OrganizationSelector from "@/components/OrganizationSelector.vue";
import EstablishmentSelector from "@/components/EstablishmentSelector.vue";
import ServiceCenterSelector from "@/components/ServiceCenterSelector.vue";

// Charts
import * as chartConfigs from "@/components/Charts/config";
import LineChart from "@/components/Charts/LineChart";

function formatYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function computeDefaultDateRange() {
  const today = new Date();
  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(today.getMonth() - 3);
  return [formatYmd(threeMonthsAgo), formatYmd(today)];
}

export default {
  name: "WeeklyReportsReportsPage",

  components: {
    flatPicker,
    BasePagination,
    OrganizationSelector,
    EstablishmentSelector,
    ServiceCenterSelector,
    LineChart,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
  },

  data() {
    return {
      dateRange: computeDefaultDateRange(),
      chartGrouping: 'weekly',
      filters: {
        organization_id: null,
        establishment_id: null,
        service_center_id: null,
        status: "VALIDATED",
      },
      pagination: {
        perPage: 25,
        currentPage: 1,
      },
      dateRangeConfig: {
        mode: "range",
        dateFormat: "Y-m-d",
        allowInput: true,
        locale: this.$flatPickrLocale ? this.$flatPickrLocale() : undefined,
      },
      lineChartOptions: {
        ...chartConfigs.lineChartOptionsBlue,
        scales: {
          ...chartConfigs.lineChartOptionsBlue.scales,
          yAxes: [{
            ...chartConfigs.lineChartOptionsBlue.scales.yAxes[0],
            ticks: {
              ...chartConfigs.lineChartOptionsBlue.scales.yAxes[0].ticks,
              callback: (value) => this.formatCurrencyShort(value),
            },
          }],
        },
      },
    };
  },

  computed: {
    ...mapGetters({
      reportData: "weeklyReportsReport/reportData",
      chartData: "weeklyReportsReport/chartData",
      meta: "weeklyReportsReport/meta",
      loading: "weeklyReportsReport/loading",
      chartLoading: "weeklyReportsReport/chartLoading",
    }),

    stats() {
      return this.meta?.stats || {
        totalSales: 0,
        totalPurchases: 0,
        totalConsumables: 0,
        netProfit: 0,
        totalWages: 0,
        totalRent: 0,
        weeklyAverageSales: 0,
        avgWagesRatio: 0,
        avgRentRatio: 0,
        avgPurchasesRatio: 0,
        avgConsumablesRatio: 0,
        avgNetProfitRatio: 0,
        numberOfReports: 0,
      };
    },

    total() {
      return this.meta?.page?.total || 0;
    },

    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },

    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },

    evolutionChartData() {
      const data = this.chartData?.evolutionOverTime || [];
      return {
        labels: data.map(d => d.date),
        datasets: [
          {
            label: this.$t('WEEKLY_REPORTS_REPORT.TOTAL_SALES'),
            data: data.map(d => d.total_sales),
            borderColor: chartConfigs.Charts.colors.theme.primary,
            backgroundColor: 'transparent',
            pointBackgroundColor: chartConfigs.Charts.colors.theme.primary,
          },
          {
            label: this.$t('WEEKLY_REPORTS_REPORT.TOTAL_PURCHASES'),
            data: data.map(d => d.total_purchases),
            borderColor: chartConfigs.Charts.colors.theme.warning,
            backgroundColor: 'transparent',
            pointBackgroundColor: chartConfigs.Charts.colors.theme.warning,
          },
          {
            label: this.$t('WEEKLY_REPORTS_REPORT.NET_PROFIT'),
            data: data.map(d => d.net_profit),
            borderColor: chartConfigs.Charts.colors.theme.success,
            backgroundColor: 'transparent',
            pointBackgroundColor: chartConfigs.Charts.colors.theme.success,
          },
        ],
      };
    },

    ratiosChartData() {
      const data = this.chartData?.evolutionOverTime || [];
      const mkDataset = (label, prop, color) => ({
        label,
        data: data.map(d => d[prop]),
        borderColor: color,
        backgroundColor: 'transparent',
        pointBackgroundColor: color,
      });
      return {
        labels: data.map(d => d.date),
        datasets: [
          mkDataset(this.$t('WEEKLY_REPORTS_REPORT.WAGES_RATIO'), 'wages_ratio', chartConfigs.Charts.colors.theme.primary),
          mkDataset(this.$t('WEEKLY_REPORTS_REPORT.RENT_RATIO'), 'rent_ratio', chartConfigs.Charts.colors.theme.warning),
          mkDataset(this.$t('WEEKLY_REPORTS_REPORT.PURCHASES_RATIO'), 'purchases_ratio', chartConfigs.Charts.colors.theme.info),
          mkDataset(this.$t('WEEKLY_REPORTS_REPORT.NET_PROFIT_RATIO'), 'net_profit_ratio', chartConfigs.Charts.colors.theme.success),
        ],
      };
    },

    ratiosLineChartOptions() {
      return {
        ...chartConfigs.lineChartOptionsBlue,
        scales: {
          ...chartConfigs.lineChartOptionsBlue.scales,
          yAxes: [{
            ...chartConfigs.lineChartOptionsBlue.scales.yAxes[0],
            ticks: {
              ...chartConfigs.lineChartOptionsBlue.scales.yAxes[0].ticks,
              callback: (value) => this.formatPercent(value),
            },
          }],
        },
      };
    },
  },

  watch: {
    "pagination.currentPage": {
      handler: "fetchData",
      immediate: false,
    },
  },

  methods: {
    ...mapActions({
      fetchReport: "weeklyReportsReport/fetchReport",
      fetchChartData: "weeklyReportsReport/fetchChartData",
    }),

    getDateParams() {
      let startDate = undefined;
      let endDate = undefined;

      if (Array.isArray(this.dateRange) && this.dateRange.length >= 1) {
        startDate = this.dateRange[0];
        endDate = this.dateRange[1] || this.dateRange[0];
      } else if (typeof this.dateRange === "string" && this.dateRange) {
        let dates = this.dateRange.split(" to ");
        if (dates.length === 1) {
          dates = this.dateRange.split(" au ");
        }
        if (dates.length === 2) {
          startDate = dates[0].trim();
          endDate = dates[1].trim();
        } else if (dates.length === 1 && dates[0]) {
          startDate = dates[0].trim();
          endDate = dates[0].trim();
        }
      }

      return { startDate, endDate };
    },

    async fetchData() {
      const { startDate, endDate } = this.getDateParams();

      const params = {
        organization_id: this.filters.organization_id || undefined,
        establishment_id: this.filters.establishment_id || undefined,
        service_center_id: this.filters.service_center_id || undefined,
        status: this.filters.status,
        start_date: startDate,
        end_date: endDate,
        page: this.pagination.currentPage,
        per_page: this.pagination.perPage,
      };

      await this.fetchReport(params);
    },

    async updateCharts() {
      const { startDate, endDate } = this.getDateParams();

      const params = {
        organization_id: this.filters.organization_id || undefined,
        establishment_id: this.filters.establishment_id || undefined,
        service_center_id: this.filters.service_center_id || undefined,
        status: this.filters.status,
        start_date: startDate,
        end_date: endDate,
        grouping: this.chartGrouping,
      };

      await this.fetchChartData(params);
    },

    applyFilters() {
      this.pagination.currentPage = 1;
      this.fetchData();
      this.updateCharts();
    },

    resetFilters() {
      this.dateRange = computeDefaultDateRange();
      this.chartGrouping = 'weekly';
      this.filters = {
        organization_id: null,
        establishment_id: null,
        service_center_id: null,
        status: "VALIDATED",
      };
      this.pagination.currentPage = 1;
      this.fetchData();
      this.updateCharts();
    },

    getSummaries(param) {
      const { columns, data } = param;
      const sums = [];

      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = this.$t('COMMON.TOTALS');
          return;
        }

        const prop = column.property;
        const sumCurrencyProps = ['total_sales', 'total_purchases', 'net_profit', 'wages', 'rent'];

        if (sumCurrencyProps.includes(prop)) {
          const total = data.reduce((sum, row) => sum + (row[prop] || 0), 0);
          sums[index] = this.formatCurrency(total);
        } else {
          sums[index] = '';
        }
      });

      return sums;
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat("fr-CA", {
        style: "currency",
        currency: "CAD",
      }).format(amount || 0);
    },

    formatCurrencyShort(amount) {
      if (amount >= 1000000) {
        return (amount / 1000000).toFixed(1) + 'M$';
      } else if (amount >= 1000) {
        return (amount / 1000).toFixed(1) + 'k$';
      }
      return amount + '$';
    },

    formatNumber(value) {
      return new Intl.NumberFormat("fr-CA").format(value || 0);
    },

    formatPercent(value) {
      return `${new Intl.NumberFormat("fr-CA", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value || 0)} %`;
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-CA');
    },
  },

  mounted() {
    this.fetchData();
    this.updateCharts();
  },
};
</script>

<style scoped>
.chart {
  min-height: 250px;
}

.category-summary-card {
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.category-summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.12) !important;
}

.category-summary-card .card-header h4 {
  font-size: 1rem;
  letter-spacing: 0.025em;
}

/* Same green as the weekly report detail view (.bg-gradient-green). */
.cat-green {
  background: linear-gradient(87deg, #2dce89, #26a96c) !important;
}
</style>
